import React, { useState, useEffect } from "react";
import {
  FileText,
  UploadCloud,
  Loader2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";

const allowedTypes = {
  cv: ["doc", "docx"],
  personal_statement: ["doc", "docx"],
  educational_doc: ["jpg", "jpeg", "pdf"],
  passport: ["jpg", "jpeg", "pdf"],
  additional_doc: ["jpg", "jpeg", "png", "pdf", "doc", "docx"],
};

const UploadStudentDocument = () => {
  const [documentType, setDocumentType] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [allowedExtensions, setAllowedExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  const createdBy = user?.id;
  const studentId = user?.id_s;

  useEffect(() => {
    if (documentType) {
      setAllowedExtensions(allowedTypes[documentType] || []);
      setDocumentFile(null);
    } else {
      setAllowedExtensions([]);
    }
  }, [documentType]);

  const validateFile = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    return allowedExtensions.includes(ext);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError("");
    if (file && !validateFile(file)) {
      setDocumentFile(null);
      setError(`❌ Invalid file type. Allowed: ${allowedExtensions.join(", ").toUpperCase()}`);
      return;
    }
    setDocumentFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!studentId || !documentType || !documentFile || !createdBy) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("student_id", studentId);
      formData.append("document_type", documentType);
      formData.append("created_by", createdBy);
      formData.append("document_file", documentFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/add-student-document`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Attach token if needed
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to upload document.");
      }

      await res.json();
      setSuccessMsg("✅ Document uploaded successfully!");
      setDocumentType("");
      setDocumentFile(null);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto p-8 space-y-6 bg-white rounded-2xl shadow-lg border border-gray-200"
    >
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">📤 Upload Your Document</h2>

      {/* Document Type */}
      <div>
        <label htmlFor="documentType" className="block text-gray-700 font-medium mb-1">
          Document Type
        </label>
        <div className="relative">
          <select
            id="documentType"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Select Document Type --</option>
            {Object.keys(allowedTypes).map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
          <FileText className="absolute left-3 top-3 text-gray-500" size={18} />
        </div>
      </div>

      {/* File Upload */}
      <div>
        <label htmlFor="documentFile" className="block text-gray-700 font-medium mb-1">
          Upload File
        </label>
        <input
          key={documentType}
          id="documentFile"
          type="file"
          onChange={handleFileChange}
          className="w-full px-4 py-2 border rounded-lg bg-gray-50 file:bg-blue-600 file:text-white file:border-none file:px-4 file:py-2 file:rounded-md file:cursor-pointer"
          accept={allowedExtensions.map((ext) => "." + ext).join(",")}
        />
        {allowedExtensions.length > 0 && (
          <p className="text-sm text-gray-500 mt-1">
            Allowed types: {allowedExtensions.join(", ").toUpperCase()}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        type="submit"
        disabled={loading}
        className={`w-full py-3 rounded-xl text-white font-semibold transition duration-300 ${
          loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={18} /> Uploading...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <UploadCloud size={18} /> Upload Document
          </div>
        )}
      </motion.button>

      {/* Feedback */}
      {error && (
        <p className="text-red-600 text-sm text-center flex items-center justify-center gap-1">
          <AlertCircle size={16} /> {error}
        </p>
      )}
      {successMsg && (
        <p className="text-green-600 text-sm text-center flex items-center justify-center gap-1">
          <CheckCircle size={16} /> {successMsg}
        </p>
      )}
    </motion.form>
  );
};

export default UploadStudentDocument;
